import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Collapse,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  getVoiceRecords,
  getApiLogs,
  VoiceRecordDebug,
  ApiLogEntry,
} from '../services/debug'

function DebugScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('debug.title')}
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t('debug.voiceRecords')} />
        <Tab label={t('debug.apiLogs')} />
      </Tabs>
      {tab === 0 && <VoiceRecordsTab />}
      {tab === 1 && <ApiLogsTab />}
    </Box>
  )
}

function VoiceRecordsTab(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const [data, setData] = useState<VoiceRecordDebug[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sort, setSort] = useState('newest')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await getVoiceRecords({
        page,
        limit: 20,
        search: search || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        sort,
      })
      setData(res.data.data)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      setError(t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [page, search, startDate, endDate, sort, t])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label={t('debug.searchTranscript')}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          type="date"
          label={t('debug.startDate')}
          value={startDate}
          onChange={e => { setStartDate(e.target.value); setPage(1) }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label={t('debug.endDate')}
          value={endDate}
          onChange={e => { setEndDate(e.target.value); setPage(1) }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('debug.sort')}</InputLabel>
          <Select
            value={sort}
            label={t('debug.sort')}
            onChange={(e: SelectChangeEvent) => setSort(e.target.value)}
          >
            <MenuItem value="newest">{t('debug.newest')}</MenuItem>
            <MenuItem value="oldest">{t('debug.oldest')}</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {t('debug.totalRecords')}: {total}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('debug.dateTime')}</TableCell>
                  <TableCell>{t('debug.userId')}</TableCell>
                  <TableCell>{t('debug.transcript')}</TableCell>
                  <TableCell>{t('debug.aiResponse')}</TableCell>
                  <TableCell>{t('debug.language')}</TableCell>
                  <TableCell>{t('debug.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('debug.noRecords')}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map(record => (
                    <TableRow key={record._id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(record.createdAt).toLocaleString(i18n.language)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {record.userId}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={record.transcript}>
                          {record.transcript}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={record.aiResponse || ''}>
                          {record.aiResponse || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.language || '-'}</TableCell>
                      <TableCell>
                        {record.status ? (
                          <Chip label={t(`debug.recordStatus.${record.status}`, { defaultValue: record.status })} size="small" color="default" />
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

function ApiLogRow({ log }: { log: ApiLogEntry }): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)

  const methodColor: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'default'> = {
    GET: 'primary',
    POST: 'success',
    PUT: 'warning',
    DELETE: 'error',
  }

  const statusColor = (status: number): 'success' | 'warning' | 'error' | 'default' => {
    if (status < 300) return 'success'
    if (status < 400) return 'warning'
    return 'error'
  }

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? t('debug.collapse') : t('debug.expand')}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {new Date(log.timestamp).toLocaleString(i18n.language)}
        </TableCell>
        <TableCell>
          <Chip
            label={log.method}
            size="small"
            color={methodColor[log.method] || 'default'}
          />
        </TableCell>
        <TableCell sx={{ maxWidth: 300 }}>
          <Typography variant="body2" noWrap title={log.url} sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {log.url}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip label={log.status} size="small" color={statusColor(log.status)} />
        </TableCell>
        <TableCell>{t('debug.msValue', { value: log.responseTime })}</TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {log.userId || '-'}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              {log.requestBody && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{t('debug.requestBody')}</Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: 'grey.100', p: 1, borderRadius: 1, overflow: 'auto', maxHeight: 200 }}
                  >
                    {JSON.stringify(log.requestBody, null, 2)}
                  </Typography>
                </Box>
              )}
              {log.responseBody && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color={log.status >= 400 ? 'error' : 'primary'}>
                    {t('debug.responseBody')}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      bgcolor: log.status >= 400 ? 'error.50' : 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300,
                    }}
                  >
                    {typeof log.responseBody === 'string' ? log.responseBody : JSON.stringify(log.responseBody, null, 2)}
                  </Typography>
                </Box>
              )}
              {log.error && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="error">{t('debug.errorLabel')}</Typography>
                  <Typography variant="body2" color="error">{log.error}</Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                {t('debug.ipUserAgent', { ip: log.ip, userAgent: log.userAgent })}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

function ApiLogsTab(): React.JSX.Element {
  const { t } = useTranslation()
  const [data, setData] = useState<ApiLogEntry[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [method, setMethod] = useState('')
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('newest')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await getApiLogs({
        page,
        limit: 20,
        method: method || undefined,
        status: status || undefined,
        search: search || undefined,
        sort,
      })
      setData(res.data.data)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      setError(t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [page, method, status, search, sort, t])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('debug.method')}</InputLabel>
          <Select
            value={method}
            label={t('debug.method')}
            onChange={(e: SelectChangeEvent) => { setMethod(e.target.value); setPage(1) }}
          >
            <MenuItem value="">{t('debug.all')}</MenuItem>
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('debug.status')}</InputLabel>
          <Select
            value={status}
            label={t('debug.status')}
            onChange={(e: SelectChangeEvent) => { setStatus(e.target.value); setPage(1) }}
          >
            <MenuItem value="">{t('debug.all')}</MenuItem>
            <MenuItem value="200">200</MenuItem>
            <MenuItem value="201">201</MenuItem>
            <MenuItem value="400">400</MenuItem>
            <MenuItem value="401">401</MenuItem>
            <MenuItem value="403">403</MenuItem>
            <MenuItem value="404">404</MenuItem>
            <MenuItem value="500">500</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={t('debug.searchUrl')}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('debug.sort')}</InputLabel>
          <Select
            value={sort}
            label={t('debug.sort')}
            onChange={(e: SelectChangeEvent) => setSort(e.target.value)}
          >
            <MenuItem value="newest">{t('debug.newest')}</MenuItem>
            <MenuItem value="oldest">{t('debug.oldest')}</MenuItem>
            <MenuItem value="slowest">{t('debug.slowest')}</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {t('debug.totalRecords')}: {total}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('debug.timestamp')}</TableCell>
                  <TableCell>{t('debug.method')}</TableCell>
                  <TableCell>{t('debug.url')}</TableCell>
                  <TableCell>{t('debug.status')}</TableCell>
                  <TableCell>{t('debug.responseTime')}</TableCell>
                  <TableCell>{t('debug.userId')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t('debug.noRecords')}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map(log => <ApiLogRow key={log._id} log={log} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default DebugScreen
